import React, { useState } from 'react';
import { Form, Formik } from 'formik';
import { AreaInput, Button, Grid, IconType, ISpruceFieldProps, RotatingIcon, SideSheet, SpruceField } from 'ui-toolkit';
import { IFetchStatus } from 'types/rest/IFetchStatus';
import GrayLine from '../shared/GrayLine';
import {
  defaultContactEditOrdersDialog,
  defaultContactForm,
} from '../../validation/clients/DefaultContactForm';
import { DefaultContactRolesField } from './DefaultContactRolesField';
import { DefaultContactEditOrdersDialog } from './DefaultContactEditOrdersDialog';
import { FormStatusWrapper } from '../shared/FormStatusWrapper/FormStatusWrapper';
import { AddContactFormAdminSection } from '../order/contacts/internalOrderContacts/AddContactFormAdminSection';
import {
  eContactEnvironment,
  eContactType,
  getContactDuplicationMessage,
} from '../../utilities/contact/duplicationErrorMessage';

interface IAddDefaultContactFormProps {
  onSubmit: (value: any) => void;
  onCancel: () => void;
  getAffectedOrders: (value: any) => void;
  affectedOrderFetchStatus: IFetchStatus;
  addFetchError?: Error;
  addFetchStatus: IFetchStatus;
  affectedOrders: number;
  onSelectAdmin: (adminId: string) => void;
}

export const AddDefaultContactForm: React.FC<IAddDefaultContactFormProps> = ({
                                                                               onSubmit,
                                                                               onCancel,
                                                                               addFetchError,
                                                                               addFetchStatus,
                                                                               affectedOrders,
                                                                               getAffectedOrders,
                                                                               affectedOrderFetchStatus,
                                                                               onSelectAdmin,
                                                                             }) => {
  const initialValues = { roles: { '': [] } };
  const [showEditOrdersDialog, setShowEditOrdersDialog] = useState(false);
  return (
    <Formik
      initialValues={initialValues}
      validateOnMount={true}
      validationSchema={
        showEditOrdersDialog ? defaultContactEditOrdersDialog : defaultContactForm
      }
      onSubmit={(values) => {
        onSubmit(values);
        setShowEditOrdersDialog(false);
      }}
      onReset={onCancel}
    >
      {({ isValid, dirty, values, handleSubmit }) => (
        <Form onSubmit={handleSubmit}>
          <SideSheet
            title={'New Contact'}
            onClose={onCancel}
            footer={
              <>
                {showEditOrdersDialog ? (
                  <>
                    {values['updateOrders'] && (
                      <Grid columnCount={2}>
                        <Button intent={'secondary'} type={'reset'}>
                          Cancel
                        </Button>
                        <Button
                          intent={'primary'}
                          type={'submit'}
                          disabled={!isValid || !dirty || addFetchStatus.fetching}
                        >
                          Save
                        </Button>
                      </Grid>
                    )}
                  </>
                ) : (
                  <SpruceField name={'updateType'}>
                    {({ field }: ISpruceFieldProps) => (
                      <Grid columnCount={2}>
                        <Button intent={'secondary'} type={'reset'}>
                          Cancel
                        </Button>
                        <Button
                          intent={'primary'}
                          type={'button'}
                          onClick={() => {
                            setShowEditOrdersDialog(true);
                            field.onChange('CREATE');
                          }}
                          disabled={!isValid || !dirty || addFetchStatus.fetching}
                        >
                          Save and Add
                        </Button>
                      </Grid>
                    )}
                  </SpruceField>
                )}
              </>
            }
          >
            <Grid>
              <FormStatusWrapper
                fetching={addFetchStatus.fetching}
                fetchedSuccess={addFetchStatus.success}
                fetchError={addFetchError}
                successMessage={
                  affectedOrders === (undefined || 0)
                    ? `Contact Added!`
                    : `Contact Added! ${affectedOrders} order(s) updated`
                }
                duplicateMessage={getContactDuplicationMessage(eContactType.Internal, eContactEnvironment.Client)}
              />
              {showEditOrdersDialog ? (
                <DefaultContactEditOrdersDialog
                  onNoClick={handleSubmit}
                  getAffectedOrders={getAffectedOrders}
                  preliminaryAffectedOrderFetchStatus={affectedOrderFetchStatus}
                />
              ) : (
                <>
                  <AddContactFormAdminSection onSelectAdmin={onSelectAdmin} />
                  <SpruceField name={'notes'}>
                    {({ field }: ISpruceFieldProps) => (
                      <AreaInput
                        {...field}
                        rows={2}
                        onBlur={() => field.onChange(field.value ? field.value.trim() : field.value)}
                        label={'Notes'}
                        placeholder={'Notes...'}
                      />
                    )}
                  </SpruceField>
                  <GrayLine />
                  <DefaultContactRolesField isValid={isValid} />
                </>
              )}
              {affectedOrderFetchStatus.success && values['updateOrders'] && (
                <>{affectedOrders} order(s) will be affected</>
              )}
              {affectedOrderFetchStatus.fetching && <RotatingIcon type={IconType.Loading} />}
            </Grid>
          </SideSheet>
        </Form>
      )}
    </Formik>
  );
};
